import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Tooltip,Typography } from '@mui/material';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getPreviewUrl,formatDate } from './DataDelivery-services-utils';
// import React, { useEffect, useState, useRef } from 'react';


// Fix for Leaflet icon issues in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Image Overlay component for rendering the preview within the AOI
export const ImageOverlay = ({ image, aoiCoords }) => {
  const map = useMap();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!aoiCoords || aoiCoords.length < 3 || !image.id) return;

    const fetchFreshPreviewUrl = async () => {
      try {
        setIsLoading(true);
        // console.log(image)
        const url = image.previewUrl
        setPreviewUrl(url);
      } catch (err) {
        console.error("Error fetching preview URL:", err);
        setError(err);
        // Fallback to the original URL if available
        if (image.preview_url) {
          setPreviewUrl(image.preview_url);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFreshPreviewUrl();
  }, [image.id, aoiCoords]);

  useEffect(() => {
    if (!aoiCoords || aoiCoords.length < 3 || !previewUrl || isLoading) return;
    
    let overlay = null;
    // console.log(image.coordinates)
    // Create bounds from the AOI coordinates
    const bounds = L.latLngBounds(image.coordinates[0].map(coord => [coord[1], coord[0]]));
    
    // Create the image overlay
    overlay = L.imageOverlay(previewUrl, bounds, {
      opacity: 1,
      interactive: true
    });

    // Add popup with image details
    overlay.bindPopup(`
      <div style="width: 200px">
        <h4 style="margin: 0 0 8px 0">${image.satellite || 'Unknown Satellite'}</h4>
        <p style="margin: 4px 0">Date: ${formatDate(image.datetime || '')}</p>
        <p style="margin: 4px 0">Cloud Cover: ${image.cloud_cover || 'N/A'}%</p>
        <p style="margin: 4px 0">Resolution: ${image.resolution || 'N/A'}m</p>
      </div>
    `);
    
    // Add the overlay to the map
    overlay.addTo(map);
    
    // Clean up on unmount
    return () => {
      if (overlay) {
        map.removeLayer(overlay);
      }
    };
  }, [map, image, aoiCoords, previewUrl, isLoading]);
  
  // Show fallback content if there's an error and no fallback URL
  if (error && !previewUrl) {
    return (
      <Tooltip title="Error loading preview">
        <L.Rectangle
          bounds={L.latLngBounds(aoiCoords.map(coord => [coord[0], coord[1]]))}
          pathOptions={{
            color: 'red',
            fillColor: 'pink',
            fillOpacity: 0.3
          }}
        />
      </Tooltip>
    );
  }
  
  return null;
};

// ImagePreviewOverlay component to manage preview visibility
export const ImagePreviewOverlay = ({ images, aoiCoords }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Select the first image by default or when images change
  useEffect(() => {
    if (images && images.length > 0) {
      setSelectedImage(images[0]);
    }
  }, [images]);
  
  if (!images || images.length === 0 || !aoiCoords || aoiCoords.length < 3) {
    return null;
  }
  
  return (
    <>
      {images.map((image, idx) => (
        <ImageOverlay key={image.id || idx} image={image} aoiCoords={aoiCoords} />
      ))}
    </>
  );
};  

const getMapCenter = (polygonCoords) => {
  if (!polygonCoords || !polygonCoords.length) return [0, 0];
  
  const lats = polygonCoords.map(coord => coord[0]);
  const lngs = polygonCoords.map(coord => coord[1]);
  
  return [
    lats.reduce((a, b) => a + b, 0) / lats.length,
    lngs.reduce((a, b) => a + b, 0) / lngs.length
  ];
};


// MapType control component
export const MapTypeControl = ({ 
  setActiveMapType, 
  onToggleGeoTIFF,
  
}) => {
  return (
    <Box sx={{ 
      position: 'absolute', 
      top: 10, 
      right: 10,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 1 
      }}>
        <Button 
          variant="contained" 
          onClick={() => setActiveMapType('street')}
          sx={{ 
            bgcolor: 'white', 
            color: 'black',
            flex: 1,
            '&:hover': {
              bgcolor: '#f5f5f5'
            }
          }}
        >
          Street
        </Button>
        <Button 
          variant="contained" 
          onClick={() => setActiveMapType('satellite')}
          sx={{ 
            bgcolor: '#f5f5f5', 
            color: 'black',
            flex: 1,
            '&:hover': {
              bgcolor: '#e0e0e0'
            }
          }}
        >
          Satellite
        </Button>

      </Box>
    </Box>
  );
};

// MapView component for rendering the entire map section
// MapView component for rendering the entire map section
export const MapView = ({ 
  polygonCoords, 
  orderData, 
  activeMapType, 
  setActiveMapType, 
  isTaskingOrder 
}) => {
  const mapRef = useRef(null);

  // Find the first image with GeoTIFF or preview
  const geoTIFFImage = orderData?.selected_images?.find(img => img.geoTIFFVisible);
  const previewImages = orderData?.selected_images?.filter(
    img => img.previewVisible === true
  );
  
  

  // Effect to fit bounds when polygon changes
  useEffect(() => {
    if (mapRef.current && polygonCoords.length > 2) {
      const bounds = L.latLngBounds(polygonCoords);
      mapRef.current.fitBounds(bounds, {
        padding: [50, 50], // Add some padding
        maxZoom: 12 // Limit max zoom
      });
    }
  }, [polygonCoords]);

  useEffect(() => {
    if (geoTIFFImage && mapRef.current) {
      const tiffLayer = L.tileLayer('http://localhost:8001/{z}/{x}/{y}.png', {
        minZoom: 8,
        maxZoom: 19,
        tms: true,
        opacity: 1,
        attribution: 'My GeoTIFF Data'
      });
      tiffLayer.addTo(mapRef.current);

      // Cleanup function
      return () => {
        if (mapRef.current) {
          mapRef.current.removeLayer(tiffLayer);
        }
      };
    }
  }, [geoTIFFImage]);

  // Helper function to render strip coordinates
  const renderStripCoordinates = () => {
    if (!isTaskingOrder || !orderData?.selected_images) return null;
    
    return orderData.selected_images.map((image, index) => {
      // Check if strip_coordinates exists and is an array with content
      if (!image.strip_coordinates || !Array.isArray(image.strip_coordinates) || image.strip_coordinates.length === 0) {
        return null;
      }
      
      // Handle different possible structures of strip_coordinates
      if (Array.isArray(image.strip_coordinates[0]) && Array.isArray(image.strip_coordinates[0][0])) {
        // Nested array structure (array of polygon arrays)
        return image.strip_coordinates.map((polygonArray, outerIndex) => {
          return polygonArray.map((polygon, polyIndex) => {
            // Convert coordinates to [lat, lng] format
            const stripCoords = polygon.map(coord => 
              Array.isArray(coord) 
                ? [coord[0], coord[1]] 
                : [coord.lat, coord.lng]
            );
            
            return (
              <Polygon 
                key={`strip-${index}-${outerIndex}-${polyIndex}`}
                positions={stripCoords}
                pathOptions={{ 
                  color: '#0088FF',
                  weight: 2,
                  fillColor: '#0088FF',
                  fillOpacity: 0.2
                }}
              />
            );
          });
        });
      } else {
        // Simpler array structure (single polygon)
        const stripCoords = image.strip_coordinates.map(coord => 
          Array.isArray(coord) 
            ? [coord[0], coord[1]] 
            : [coord.lat, coord.lng]
        );
        
        return (
          <Polygon 
            key={`strip-${index}`}
            positions={stripCoords}
            pathOptions={{ 
              color: '#0088FF',
              weight: 2,
              fillColor: '#0088FF',
              fillOpacity: 0.2
            }}
          />
        );
      }
    });
  };

  return (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
      {polygonCoords.length > 0 ? (
        <MapContainer 
          ref={mapRef}
          center={getMapCenter(polygonCoords)} 
          zoom={8} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url={activeMapType === 'street' 
              ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            }
            attribution={activeMapType === 'street'
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              : '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            }
          />
          
          {/* Polygon always rendered */}
          <Polygon 
            positions={polygonCoords}
            pathOptions={{ 
              color: 'rgba(255, 0, 0, 0.5)',
              weight: 2,
              fillColor: 'rgba(255, 0, 0, 0.2)',
              fillOpacity: 0
            }}
          />

          {/* Strip Coordinates for Tasking Orders */}
          {isTaskingOrder && renderStripCoordinates()}

          {/* Preview Overlay - only for first visible preview image */}
          {previewImages && previewImages.length > 0 && (
  <ImagePreviewOverlay 
    images={previewImages} 
    aoiCoords={polygonCoords} 
  />
)}


          <MapTypeControl 
            setActiveMapType={setActiveMapType}
          />
        </MapContainer>
      ) : (
        <Box sx={{ 
          height: '100%', 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: '#f5f5f5'
        }}>
          No coordinates available
        </Box>
      )}
    </Box>
  );
};